/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectionManagementWizard/rejectionManagementEmailWizard"/>
import { Checkbox, FlexLayout, ResponsiveGridLayout, Switch, TextArea, Text, TextField, ButtonGroup, Button, IconActionDeleteRegular, GridLayoutItem, FlexContent, ListItem } from "@medius/ui-controls";
import { getLabelTranslation, translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import * as React from "react";
import { IEmailSettings, IEmailContent, SupplierEmailForRejections, DefaultSupplierEmail, IReasonCode, RejectionEmailContentRequestDto, ReasonCodeType, RejectionEmailSupportedAttachmentExtensions } from "./interfaces";
import { getDefaultSupplierEmail, getEmailSettings, getRejectionEmailAiContent } from "./services";
import { Control, Controller, ControllerRenderProps, FieldValues, FormState, UseFormClearErrors, UseFormHandleSubmit, UseFormReset, UseFormWatch, UseFormSetValue, useFieldArray, UseFormGetValues } from "react-hook-form";
import { RejectionManagementEmailWizardFormState } from "./useRejectionManagementEmailForm";
import { getRegex } from "Core/Medius.Core.Web/Scripts/Medius/core/validation/email";
import { IconColorAi } from '@medius/ui-controls';
import { EmailAttachmentItem, EmailAttachmentsDropzone } from "./emailAttachmentsDropzone";
import { isEmailSendingDropzoneEnabledInRejectionManagement } from "Core/Medius.Core.Web/Scripts/Medius/core/featureToggle";

type RejectionManagementEmailContext = {
    handleSubmit: UseFormHandleSubmit<FieldValues>;
    control: Control<FieldValues>;
    reset: UseFormReset<FieldValues>;
    defaultState: RejectionManagementEmailWizardFormState;
    formState: FormState<FieldValues>;
    watch: UseFormWatch<FieldValues>;
    clearErrors: UseFormClearErrors<FieldValues>;
    setValue?: UseFormSetValue<FieldValues>;
    getValues?: UseFormGetValues<FieldValues>;
};

type RejectionManagementEmailWizardProps = {
    formContext?: RejectionManagementEmailContext;
    documentId: number;
    supplierEmailForRejections: SupplierEmailForRejections;
    selection: IReasonCode[];
    rejectionActions: ListItem[];
};

const defaultLanguageCode = "en-US";
const MAX_ATTACHMENTS_BYTES = 25_000_000; // 25 MB

export const RejectionManagementEmailWizard = ({
    formContext,
    documentId,
    supplierEmailForRejections,
    selection,
    rejectionActions
}: RejectionManagementEmailWizardProps) => {
    const { control, watch, clearErrors, setValue, reset, getValues } = formContext;
    const sendToSupplier = watch("sendToSupplier");
    const [emailSettings, setEmailSettings] = React.useState<IEmailSettings | null>(null);
    const [selectedLanguage, setSelectedLanguage] = React.useState<string>(defaultLanguageCode);
    const [emailContentCache, setEmailContentCache] = React.useState<Record<string, IEmailContent>>({});
    const [userHasEmail, setUserHasEmail] = React.useState<boolean>(true);
    const toEmailReason = (rc: IReasonCode) => {
        const action = rc.rejectionActionId
            ? rejectionActions.find(ra => ra.value === String(rc.rejectionActionId))
            : undefined;

        return {
            reasonCode: rc.reasonCode,
            reasonCodeDescription: rc.description,
            rejectionActionCode: action?.header ?? null,
            rejectionActionDescription: action?.subText ?? null,
        };
    };

    const isDropzoneEnabled = isEmailSendingDropzoneEnabledInRejectionManagement();
    React.useEffect(() => {
        const fetchEmailContent = async (documentId: number, languageCode: string) => {
            const invoiceRejectionReasons = selection
                .filter(rc => rc.types.includes(ReasonCodeType.External))
                .map(toEmailReason);

            const rejectionEmailContentRequest: RejectionEmailContentRequestDto = {
                languageCode,
                invoiceRejectionReasons,
            };

            return await getRejectionEmailAiContent(documentId, rejectionEmailContentRequest);
        };

        if (selectedLanguage && documentId) {
            const cacheKey = `${documentId}-${selectedLanguage}`;

            if (!emailContentCache[cacheKey]) {
                fetchEmailContent(documentId, selectedLanguage).then(content => {
                    if (content) {
                        setEmailContentCache(prev => ({
                            ...prev,
                            [cacheKey]: content
                        }));

                        if (setValue) {
                            setValue("content", content.body || "", { shouldValidate: false });
                            setValue("messageSubject", content.subject || "", { shouldValidate: false });
                        }
                    }
                });
            } else {
                const cachedContent = emailContentCache[cacheKey];

                if (setValue && cachedContent) {
                    setValue("content", cachedContent.body || "", { shouldValidate: false });
                    setValue("messageSubject", cachedContent.subject || "", { shouldValidate: false });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId, selectedLanguage, setValue]);

    const currentEmailContent = React.useMemo(() => {
        if (selectedLanguage && documentId) {
            const cacheKey = `${documentId}-${selectedLanguage}`;
            return emailContentCache[cacheKey];
        }
        return null;
    }, [selectedLanguage, documentId, emailContentCache]);

    const updateCachedContent = React.useCallback((field: 'subject' | 'body', value: string) => {
        if (selectedLanguage && documentId) {
            const cacheKey = `${documentId}-${selectedLanguage}`;
            const currentCache = emailContentCache[cacheKey];

            if (currentCache) {
                setEmailContentCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        ...currentCache,
                        [field]: value
                    }
                }));
            } else {
                setEmailContentCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        subject: field === 'subject' ? value : '',
                        body: field === 'body' ? value : ''
                    }
                }));
            }
        }
    }, [selectedLanguage, documentId, emailContentCache]);

    React.useEffect(() => {
        if (currentEmailContent && setValue) {
            const currentContentValue = watch("content");
            const currentSubjectValue = watch("messageSubject");

            if (!currentContentValue) {
                setValue("content", currentEmailContent.body || "", { shouldValidate: false });
            }

            if (!currentSubjectValue) {
                setValue("messageSubject", currentEmailContent.subject || "", { shouldValidate: false });
            }
        }
    }, [currentEmailContent, setValue, watch]);

    React.useEffect(() => {
        const fetchEmailSettings = async () => {
            const settings = await getEmailSettings(documentId);
            setEmailSettings(settings);

            const hasSupplierLanguage = settings?.supplierLanguageCode && settings.supplierLanguageCode !== defaultLanguageCode;
            if (hasSupplierLanguage) {
                setSelectedLanguage(settings.supplierLanguageCode);
            } else {
                setSelectedLanguage(defaultLanguageCode);
            }
            if (!settings.currentUserEmail?.trim()) {
                setUserHasEmail(false);
                setValue('sendCopy', false);
            }
        };
        fetchEmailSettings();
    }, [setValue]);

    const handleSendToSupplierChange = (e: any, field: ControllerRenderProps) => {
        if (e.value === false)
            clearErrors();
        field.onChange(e.value);
    };

    const EmailLanguageButtons = () => {
        if (!emailSettings) return null;

        const userLanguageCode = emailSettings.currentUserLanguageCode;
        const hasSupplierLanguage = emailSettings.supplierLanguageCode && emailSettings.supplierLanguageCode !== defaultLanguageCode;
        const buttons: { code: string; label: string; testId: string; key: string }[] = [];

        if (hasSupplierLanguage) {
            buttons.push({
                code: emailSettings.supplierLanguageCode!,
                label: translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/buttonSupplierLanguage"),
                key: `${documentId}-${emailSettings.supplierLanguageCode!}`,
                testId: "supplier-language-button"
            });
        }

        buttons.push({
            code: defaultLanguageCode,
            label: translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/buttonEnglish"),
            key: "english-language-button",
            testId: "english-language-button"
        });

        if (emailSettings.currentUserLanguageCode !== defaultLanguageCode) {
            buttons.push({
                code: userLanguageCode,
                label: translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/buttonMyLanguage"),
                key: "my-language-button",
                testId: "my-language-button",
            });
        }

        return (
            buttons.length > 1 &&
            <ButtonGroup layout="row">
                {buttons.map(({ code, label, testId, key }) => (
                    <Button
                        key={key}
                        height="small"
                        disabled={!sendToSupplier}
                        variant={selectedLanguage === code ? "secondary" : "tertiary"}
                        onClick={() => setSelectedLanguage(code)}
                        data-testid={testId}
                        label={label}
                    />
                ))}
            </ButtonGroup>
        );
    };

    const { fields: emailToFields, append: appendEmailTo, remove: removeEmailTo } = useFieldArray({
        control,
        name: "emailTo"
    });

    const { fields: carbonCopyFields, append: appendCarbonCopy, remove: removeCarbonCopy } = useFieldArray({
        control,
        name: "carbonCopy"
    });

    const validateEmail = (value: string): boolean | string => {
        const emailRegex = getRegex();
        return emailRegex.test(value) || translate("#Core/defaultValidation_EMAILATTRIBUTE");
    };


    const buildEmailTo = (supplierEmailForRejections: SupplierEmailForRejections, response: DefaultSupplierEmail): { value: string }[] => {
        if (supplierEmailForRejections === SupplierEmailForRejections.sendersEmailAddress) {
            const email = response.emailSenderAddress?.trim();
            return email ? [{ value: email }] : [{ value: "" }];
        }

        const cleaned = Array.from(
            new Set((response.supplierContactPersons ?? [])
                .map(e => e?.trim())
                .filter(Boolean) as string[])
        );

        return cleaned.length > 0 ? cleaned.map(e => ({ value: e })) : [{ value: "" }];
    };

    React.useEffect(() => {
        const fetchDefaultSupplierEmail = async () => {
            const response = await getDefaultSupplierEmail(documentId);

            const emailTo = buildEmailTo(supplierEmailForRejections, response);

            reset(
                { ...getValues(), emailTo },
                { keepDirtyValues: true }
            );

        };
        fetchDefaultSupplierEmail();
    }, [documentId, supplierEmailForRejections, reset, getValues]);

    return (
        <ResponsiveGridLayout
            maxColumns={1}
            columnWidth={7}
            itemRowSpacing="small"
        >
            {emailToFields.map((field, index) => (
                <FlexLayout key={field.id} direction="row" verticalAlignment="bottom" width="fill" wrapItems={false} itemColumnSpacing="small" spacerMode="off" itemRowSpacing="small">
                    <FlexContent>
                        <Controller
                            name={`emailTo.${index}.value`}
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (!sendToSupplier) return true;
                                    return value ? validateEmail(value) : index > 0 ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE");
                                }
                            }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    isRequired={index === 0}
                                    disabled={!sendToSupplier}
                                    fieldLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/emailTo")}
                                    errorMessages={fieldState.error && sendToSupplier ? [fieldState.error.message || ""] : []}
                                    inputProps={{
                                        onChange: (e) => field.onChange(e.target.value),
                                        value: field.value,
                                        height: "custom"
                                    }}
                                    data-testid={`rejection-management-wizard-email-to${index > 0 ? `-${index}` : ""}`}
                                />
                            )}
                        />
                    </FlexContent>
                    {index > 0 && (
                        <FlexLayout spacerMode="off" verticalAlignment="center" width="content">
                            <Button
                                icon={<IconActionDeleteRegular />}
                                onClick={() => removeEmailTo(index)}
                                label={translate("#Core/delete")}
                                hideLabel
                                height="small"
                                disabled={!sendToSupplier}
                                variant="inline"
                            />
                        </FlexLayout>
                    )}
                </FlexLayout>
            ))}
            <GridLayoutItem columnSpan={1} column={1}>
                <FlexLayout spacerMode="on" spacerSize="small" width="content">
                    <Button
                        label={translate("#Core/add")}
                        data-testid="rejection-management-wizard-add-to-address-button"
                        variant="secondary"
                        height="small"
                        disabled={!sendToSupplier}
                        width="content"
                        onClick={() => appendEmailTo({ value: "" })}
                    />
                </FlexLayout>
            </GridLayoutItem>
            {
                carbonCopyFields.map((field, index) => (
                    <FlexLayout key={field.id} direction="row" verticalAlignment="bottom" width="fill" wrapItems={false} itemColumnSpacing="small" spacerMode="on" spacerSize="small">
                        <FlexContent>
                            <Controller
                                name={`carbonCopy.${index}.value`}
                                control={control}
                                rules={{
                                    validate: (value) => {
                                        if (!sendToSupplier) return true;
                                        return value ? validateEmail(value) : true;
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        isRequired={false}
                                        disabled={!sendToSupplier}
                                        fieldLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/carbonCopy")}
                                        errorMessages={fieldState.error && sendToSupplier ? [fieldState.error.message || ""] : []}
                                        inputProps={{
                                            onChange: (e) => field.onChange(e.target.value),
                                            value: field.value,
                                            height: "small"
                                        }}
                                        data-testid={`rejection-management-wizard-carbonCopy${index > 0 ? `-${index}` : ""}`}
                                    />
                                )}
                            />
                        </FlexContent>
                        {carbonCopyFields.length > 1 && (
                            <FlexLayout spacerMode="off" verticalAlignment="center" width="content">
                                <Button
                                    icon={<IconActionDeleteRegular />}
                                    onClick={() => removeCarbonCopy(index)}
                                    label={translate("#Core/delete")}
                                    disabled={!sendToSupplier}
                                    hideLabel
                                    height="small"
                                    variant="inline"
                                />
                            </FlexLayout>
                        )}
                    </FlexLayout>
                ))}
            <GridLayoutItem columnSpan={1} column={1}>
                <FlexLayout spacerMode="on" spacerSize="small" width="content">
                    <Button
                        label={translate("#Core/add")}
                        data-testid="rejection-management-wizard-add-cc-address-button"
                        variant="secondary"
                        height="small"
                        width="content"
                        disabled={!sendToSupplier}
                        onClick={() => appendCarbonCopy({ value: "" })}
                    />
                </FlexLayout>
            </GridLayoutItem>
            <Controller
                name="sendCopy"
                control={control}
                render={({ field }) => (
                    <FlexLayout spacerMode="on" spacerSize="small" width="content">
                        <Checkbox
                            disabled={!sendToSupplier || !userHasEmail}
                            label={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendMeACopy")}
                            onChange={(e) => field.onChange(e.target.value)}
                            value={field.value}
                            data-testid="rejection-management-wizard-send-copy-checkbox"
                        />
                    </FlexLayout>
                )}
            />

            {EmailLanguageButtons()}

            <Controller
                name="messageSubject"
                control={control}
                rules={{
                    validate: (value) => !sendToSupplier ? true : value ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE")
                }}
                render={({ field, fieldState }) => (
                    <FlexLayout spacerMode="on" spacerSize="small" width="fill">
                        <FlexContent>
                            <TextField
                                isRequired
                                disabled={!sendToSupplier}
                                fieldLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/messageSubject")}
                                errorMessages={fieldState.error ? [fieldState.error.message || ""] : []}
                                inputProps={{
                                    onChange: (e) => {
                                        const newValue = e.target.value;
                                        field.onChange(newValue);
                                        updateCachedContent('subject', newValue);
                                    },
                                    value: currentEmailContent?.subject || "",
                                    height: "small",
                                }}
                                data-testid="rejection-management-wizard-message-subject"
                            />
                        </FlexContent>
                    </FlexLayout>
                )}
            />
            <Controller
                name="content"
                control={control}
                rules={{
                    validate: (value) => !sendToSupplier ? true : value ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE")
                }}
                render={({ field, fieldState }) => (
                    <FlexLayout direction="row" itemRowSpacing="vsmall" height="fill" spacerMode="on" spacerSize="small">
                        <TextArea
                            width="fill"
                            height="custom"
                            onChange={(e) => {
                                const newValue = e.target.value;
                                field.onChange(newValue);
                                updateCachedContent('body', newValue);
                            }}
                            value={currentEmailContent?.body || ""}
                            disabled={!sendToSupplier}
                            invalid={!!fieldState.error}
                            data-testid="rejection-management-wizard-email-content"
                            className="email-content"
                        />
                    </FlexLayout>
                )} />
            <FlexLayout direction='row' verticalAlignment='center' itemColumnSpacing="small" spacerMode="on" spacerSize="vsmall">
                <IconColorAi />
                <Text variant="small">{translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/generatedByMediusCopilot")}</Text>
            </FlexLayout>
            <FlexLayout direction="column" itemRowSpacing="small" spacerMode="on" spacerSize="small">
                <Text variant="heading3">{translate("#Core/attachments")}</Text>
                {isDropzoneEnabled && <Text variant="paragraph">{getLabelTranslation("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/maxTotalAttachmentsSize", `${MAX_ATTACHMENTS_BYTES / 1_000_000}MB`)}</Text>}
                <Text variant="paragraph">{translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/invoiceImageWillBeAttached")}</Text>
            </FlexLayout>
            {isDropzoneEnabled && (
                <Controller
                    name="attachments"
                    control={control}
                    render={({ field }) => (
                        <EmailAttachmentsDropzone
                            attachments={(field.value as EmailAttachmentItem[]) || []}
                            onAttachmentsChange={(nextAttachments) => field.onChange(nextAttachments)}
                            maxSize={MAX_ATTACHMENTS_BYTES}
                            maxFiles={null}
                            acceptedTypes={RejectionEmailSupportedAttachmentExtensions}
                            disabled={!sendToSupplier}
                        />
                    )}
                />
            )}
            <Controller
                name="sendToSupplier"
                control={control}
                render={({ field }) => (
                    <FlexLayout spacerMode="on" spacerSize="small" width="content">
                        <Switch
                            leftLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendEmailToSupplier")}
                            value={!!field.value}
                            onChange={(e) => handleSendToSupplierChange(e, field)}
                            data-testid="rejection-management-wizard-send-to-supplier-switch"
                        />
                    </FlexLayout>
                )}
            />
        </ResponsiveGridLayout>
    );
};